/* ==========================================================================
   Satjeevan — Main Product Information tabs/accordion/pincode JS
   (main-product-info.js)

   Three independent, self-contained interactions:
     1. Mobile pill tab switcher
     2. Desktop accordion (multiple sections can be open at once, matching
        the reference's independent .acc-item toggling)
     3. Pincode "Check" button — regex only confirms the input LOOKS like a
        6-digit pincode; it can't tell a real Indian pincode from a made-up
        one or a foreign postal code that happens to also be 6 digits. To
        actually answer "is this deliverable", a valid-format pincode is
        verified against India Post's own public records
        (api.postalpincode.in — free, no key, India-only data) before
        showing success. Anything that isn't a real, existing Indian
        pincode — wrong number or outside India — reports as not
        serviceable instead of a false "available".
   ========================================================================== */

(function () {
  'use strict';

  var INIT_ATTR = 'data-sj-mpi-info-init';

  function initTabs(root) {
    var tabs = root.querySelectorAll('[data-sj-mpi-tab]');
    var panels = root.querySelectorAll('[data-sj-mpi-panel]');

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-sj-mpi-tab');

        tabs.forEach(function (t) {
          var isActive = t === tab;
          t.classList.toggle('is-active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        panels.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.getAttribute('data-sj-mpi-panel') === target);
        });
      });
    });
  }

  function initAccordion(root) {
    var heads = root.querySelectorAll('[data-sj-mpi-acc-head]');

    heads.forEach(function (head) {
      head.addEventListener('click', function () {
        var item = head.closest('.sj-mpi-acc-item');
        if (!item) return;

        var isOpen = item.classList.toggle('is-open');
        var sign = head.querySelector('.sj-mpi-acc-sign');
        if (sign) {
          sign.textContent = isOpen ? '−' : '+';
        }
      });
    });
  }

  var PINCODE_RESULT_DURATION_MS = 4000;
  var PINCODE_RESULT_FADE_MS = 250;
  var PINCODE_LOOKUP_URL = 'https://api.postalpincode.in/pincode/';
  var PINCODE_LOOKUP_TIMEOUT_MS = 6000;

  function initPincode(root) {
    var buttons = root.querySelectorAll('[data-sj-mpi-pincode-check]');

    buttons.forEach(function (button) {
      var hideTimer = null;
      var fadeTimer = null;
      var pendingController = null;

      function showResult(result, text, state) {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
        if (fadeTimer) {
          clearTimeout(fadeTimer);
          fadeTimer = null;
        }

        result.textContent = text;
        result.setAttribute('data-state', state);
        result.removeAttribute('hidden');
        result.classList.remove('sj-mpi-pincode-result--hide');

        // The "checking" state stays up until the lookup resolves — only
        // the final states (ok/error/notfound) auto-hide.
        if (state === 'checking') return;

        hideTimer = setTimeout(function () {
          result.classList.add('sj-mpi-pincode-result--hide');
          hideTimer = null;

          fadeTimer = setTimeout(function () {
            result.setAttribute('hidden', '');
            fadeTimer = null;
          }, PINCODE_RESULT_FADE_MS);
        }, PINCODE_RESULT_DURATION_MS);
      }

      button.addEventListener('click', function () {
        var wrapper = button.closest('.sj-mpi-pincode-row');
        var input = wrapper ? wrapper.querySelector('[data-sj-mpi-pincode-input]') : null;
        var result = wrapper ? wrapper.parentElement.querySelector('[data-sj-mpi-pincode-result]') : null;
        if (!input || !result) return;

        var value = input.value.trim();
        var successMessage = button.getAttribute('data-success-message') || '✓ Delivery available — arrives in 3-5 days';
        var errorMessage = button.getAttribute('data-error-message') || 'Please enter a valid 6-digit pincode';
        var notfoundMessage = button.getAttribute('data-notfound-message') || "Sorry, we currently don't deliver to this pincode";

        // Format check first — no need to hit the network for obviously
        // malformed input (also covers anything that isn't even 6 digits,
        // like most non-Indian postal codes).
        if (!/^[1-9][0-9]{5}$/.test(value)) {
          showResult(result, errorMessage, 'error');
          return;
        }

        // A pending lookup for this same button is superseded — abort it
        // rather than let a stale response land after a newer one.
        if (pendingController) {
          pendingController.abort();
        }

        var controller = ('AbortController' in window) ? new AbortController() : null;
        pendingController = controller;
        button.disabled = true;
        showResult(result, 'Checking availability…', 'checking');

        var timeoutId = setTimeout(function () {
          if (controller) controller.abort();
        }, PINCODE_LOOKUP_TIMEOUT_MS);

        fetch(PINCODE_LOOKUP_URL + encodeURIComponent(value), {
          signal: controller ? controller.signal : undefined
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Pincode lookup failed');
            return response.json();
          })
          .then(function (data) {
            clearTimeout(timeoutId);
            if (pendingController !== controller) return; // superseded
            pendingController = null;
            button.disabled = false;

            var record = Array.isArray(data) ? data[0] : null;
            var found = !!record && record.Status === 'Success' && Array.isArray(record.PostOffice) && record.PostOffice.length > 0;

            if (found) {
              showResult(result, successMessage, 'ok');
            } else {
              showResult(result, notfoundMessage, 'notfound');
            }
          })
          .catch(function () {
            clearTimeout(timeoutId);
            if (pendingController !== controller) return; // superseded
            pendingController = null;
            button.disabled = false;

            // Network/timeout/API failure — never fall back to claiming
            // delivery is available when we couldn't actually verify it.
            showResult(result, "Couldn't verify delivery — please try again", 'error');
          });
      });
    });
  }

  function init() {
    var sections = document.querySelectorAll('.sj-mpi-wrap:not([' + INIT_ATTR + '])');
    sections.forEach(function (section) {
      section.setAttribute(INIT_ATTR, '1');
      initTabs(section);
      initAccordion(section);
      initPincode(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
